import { Box, Typography, useMediaQuery } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import type DashboardStore from "../DashboardStore";
import { GRID_ARRANGE_MEDIA_QUERY } from "./grid";

/**
 * The controls for an arrangement in progress. Edits are staged: nothing other
 * people see changes until Save.
 *
 * Rendered in place of the filter row, so it shares the header's stickiness
 * and the filters cannot reload every card mid-arrangement.
 */
export const DashboardLayoutToolbar = observer(
    ({ dashboardStore }: { dashboardStore: DashboardStore }) => {
        const layoutStore = dashboardStore.dashboardLayoutStore;
        const messageStore = useStore(MessageStore);
        const isWideEnoughToArrange = useMediaQuery(GRID_ARRANGE_MEDIA_QUERY);

        if (!layoutStore.isEditing) return null;

        const save = async () => {
            const saved = await layoutStore.save();
            if (saved) return;

            messageStore.logError(
                "The dashboard layout could not be saved. Try again, or cancel to discard your changes.",
            );
        };

        return (
            <Box
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                    // Matches the filter row so the header does not shift.
                    padding: theme.spacing(1),
                    borderRadius: 2,
                    backgroundColor: theme.palette.primary[50],
                    border: `1px solid ${theme.palette.primary[200]}`,
                })}
            >
                <Typography
                    sx={{ fontWeight: 600, flexGrow: 1, paddingLeft: 1 }}
                >
                    {isWideEnoughToArrange
                        ? "Drag a card by its top left corner to move it, or by its bottom right corner to resize it. Filters are unavailable until you save or cancel."
                        : "Arranging needs a wider window. Widen the window to keep arranging and save, or cancel."}
                </Typography>

                <AcxButton
                    color="primary"
                    variant="text"
                    disabled={layoutStore.isSaving}
                    onClick={layoutStore.cancelEditing}
                    size="small"
                >
                    Cancel
                </AcxButton>
                <AcxButton
                    color="primary"
                    // A draft is saved only from a window wide enough to see
                    // the grid it was arranged on.
                    disabled={
                        !isWideEnoughToArrange ||
                        !layoutStore.isDirty ||
                        layoutStore.isSaving
                    }
                    loading={layoutStore.isSaving}
                    onClick={() => void save()}
                >
                    Save layout
                </AcxButton>
            </Box>
        );
    },
);

export default DashboardLayoutToolbar;
